import React, { useState, useEffect } from 'react';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie'
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PaginationControls from '../../utils/PaginationControls';
import { useTimezone } from '../../utils/TimezoneContext';

const AdminIamUserManagement = () => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  const { formatDateTime } = useTimezone();

  // State variables for IAM users listing
  const [iamUsers, setIamUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // State variables for form management
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedPermissionSets, setSelectedPermissionSets] = useState([]);
  const [availablePermissionSets, setAvailablePermissionSets] = useState([]);
  
  // Delete state - updated for reusable modal
  const [deleteUser, setDeleteUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // API base URL
  const API_BASE_URL = '/pceapi/administration';

  // HTTP headers with authorization
  const headers = {
    Authorization: accessToken
  };

  // Fetch IAM users on component mount and when dependencies change
  useEffect(() => {
    fetchIamUsers();
  }, [page, size, search, refreshTrigger]);

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Fetch available permission sets when form is shown
  useEffect(() => {
    if (showForm) {
      fetchAvailablePermissionSets();
    }
  }, [showForm]);

  // Load initial data if editing
  useEffect(() => {
    if (editingUser) {
      setEmail(editingUser.email || '');
      setName(editingUser.name || '');
      setSelectedPermissionSets(editingUser.permission_set_ids || []);
    } else {
      setEmail('');
      setName('');
      setSelectedPermissionSets([]);
    }
  }, [editingUser]);

  // Fetch IAM users with pagination and search
  const fetchIamUsers = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading IAM users');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/list`, 
        {
          page,
          size,
          search: search || undefined
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load IAM users');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setIamUsers(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching IAM users:', error);
      setPopupErrorMessage(error.message || 'Failed to load IAM users');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch available permission sets for dropdown
  const fetchAvailablePermissionSets = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading available permission sets');
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/iam-users/get-permission-sets`,
        { headers }
      );
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load available permission sets');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setAvailablePermissionSets(data.data || []);
    } catch (error) {
      console.error('Error fetching permission sets:', error);
      setPopupErrorMessage(error.message || 'Failed to load available permission sets');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim()) {
      setPopupErrorMessage('Email is required');
      setIsErrorPopupVisible(true);
      return;
    }
  
    if (!name.trim()) {
      setPopupErrorMessage('Name is required');
      setIsErrorPopupVisible(true);
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setPopupErrorMessage('Please enter a valid email address');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingUser ? 'Updating IAM user' : 'Creating IAM user');
    
    try {
      const formData = {
        email: email.trim(),
        name: name.trim(),
        permission_set_ids: selectedPermissionSets
      };
      
      let response;
      if (editingUser) {
        response = await axios.post(
          `${API_BASE_URL}/iam-users/update/${editingUser.user_id}`, 
          formData,
          { headers }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/iam-users/create`, 
          formData,
          { headers }
        );
      }
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || (editingUser ? 'Failed to update IAM user' : 'Failed to create IAM user'));
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || (editingUser ? 'IAM user updated successfully' : 'IAM user created successfully'));
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
      setShowForm(false);
    } catch (error) {
      console.error('Submit error:', error);
      setPopupErrorMessage(error.message || (editingUser ? 'Failed to update IAM user' : 'Failed to create IAM user'));
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };
  
  // Delete handler - updated for reusable modal
  const handleDelete = async () => {
    if (!deleteUser) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Deleting IAM user');
    setShowDeleteConfirm(false);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/iam-users/delete/${deleteUser.user_id}`,
        {},
        { headers }
      );
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || 'Failed to delete IAM user');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || 'IAM user deleted successfully');
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Delete error:', error);
      setPopupErrorMessage(error.message || 'Failed to delete IAM user');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setDeleteUser(null);
    }
  };
  
  // Toggle permission set selection
  const togglePermissionSet = (id) => {
    if (selectedPermissionSets.includes(id)) {
      setSelectedPermissionSets(prev => prev.filter(setId => setId !== id));
    } else {
      setSelectedPermissionSets(prev => [...prev, id]);
    }
  };

  // Open form for editing a user
  const handleOpenEditForm = (user) => {
    setEditingUser(user);
    setShowForm(true);
  };

  // Open delete confirmation dialog - updated for reusable modal
  const handleOpenDeleteConfirm = (user) => {
    setDeleteUser(user);
    setShowDeleteConfirm(true);
  };

  // Popup close handlers
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };

  // Helper to get permission set name from id
  const getPermissionSetName = (id) => {
    const set = availablePermissionSets.find(set => set.id === id);
    return set ? set.name : 'Unknown';
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="main-window">
      <AdminTopNav />
      <div className="container-fluid">
        <div className="row">
          <AdminSideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">IAM Users <span className="ev-light-reg">({total})</span></p>
                {!showForm && ( 
                  <button 
                    className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg" 
                    onClick={() => {
                      setEditingUser(null);
                      setEmail('');
                      setName('');
                      setSelectedPermissionSets([]);
                      setShowForm(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create IAM User
                  </button>
                )}
              </div>
              <p className="ev-message-card2 mb-4">Manage IAM users and their associated permission sets.</p>

              {/* Show form if in edit/create mode, otherwise show list */}
              {showForm ? (
                <div className="ev-card-2 w-100 ev-padding-xlarge">
                  <p className="ev-med-p-blue mb-4">
                    {editingUser ? 'Edit User' : 'Create User'}
                  </p>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-5">
                        <div className="form-group mb-3">
                          <label className="ev-reg-p-dark mb-1">Email <span className="text-danger">*</span></label>
                          {editingUser ? (
                              <input type="email" className="formField3"  value={email} onChange={(e) => setEmail(e.target.value)} disabled  required/>
                            ) : (
                              <input type="email"  className="formField3" value={email} onChange={(e) => setEmail(e.target.value)}required/>
                            )}
                        </div>
                        
                        <div className="form-group mb-4">
                          <label className="ev-reg-p-dark mb-1">Name <span className="text-danger">*</span></label>
                          <input type="text" className="formField3" value={name} onChange={(e) => setName(e.target.value)} required/>
                        </div>
                        
                        {!editingUser && (
                          <div className="alert alert-info">
                            <i className="bi bi-info-circle me-1"></i><b>Autogenerated Password</b><br />
                            Upon user creation, a temporary password will be automatically sent to the user's email address.
                          </div>
                        )}
                        
                        <div className="mt-4 text-end">
                          <button type="submit" className="ev-btn-blue-fill-round ev-bold-reg" >
                          <i className="bi bi-person"></i> {editingUser ? 'Update User' : 'Create User'}
                          </button>
                          <span>&nbsp;</span>
                          <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ms-2" onClick={() => setShowForm(false)}>Cancel</button>
                        </div>
                      </div>
                      
                      <div className="col-7">
                        <label className="ev-reg-p-dark mb-1">Permission Sets</label>
                        <div className="ev-card-3 scrollable-element vh-50">
                          {availablePermissionSets.length === 0 ? (
                            <p className="text-muted ev-small-p-dark">
                              {isProcessingPopupVisible ? 'Loading permission sets' : 'No permission sets available'}
                            </p>
                          ) : (
                            availablePermissionSets.map(set => (
                              <div key={set.id} className="mb-2 d-flex">
                                <input
                                  className="form-check-input mt-1"
                                  type="checkbox"
                                  id={`set-${set.id}`}
                                  checked={selectedPermissionSets.includes(set.id)}
                                  onChange={() => togglePermissionSet(set.id)}
                                />
                                <label className="ev-message-card3 d-flex justify-content-between align-items-center avatar-tooltip ms-2 ev-btn-small ps-3 pe-1" htmlFor={`set-${set.id}`} data-tooltip={set.name}>
                                  <div className="gap-2">
                                    <TrimContent text={set.name} maxLength={40} /> &nbsp;
                                  </div>
                                  <div className="text-end">
                                    <span className={`badge ${set.is_active ? 'bg-primary' : 'bg-secondary'} ev-btn-small`}>
                                      {set.is_active ? 'enabled' : 'disabled'}
                                    </span>
                                  </div>
                                </label>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="mt-3">
                          <p className="ev-small-p-dark mb-0">Selected: {selectedPermissionSets.length} permission sets</p>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              ) : (
                /* IAM Users List */
                <div className="ev-card-4 w-100 ev-padding-large">
                  <div className="row mt-1 mb-0">
                    <div className="col-6 position-relative">
                      <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search by email or name"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onKeyPress={handleSearchKeyPress}
                      />
                      <button 
                        className="ev-btn-lightblue-fill-round search-bar-button" 
                        onClick={handleSearch}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    
                    <div className="col-6 text-end">
                      <button 
                        className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
                        onClick={() => setRefreshTrigger(prev => prev + 1)}
                      ><i className="bi bi-arrow-repeat"></i> Refresh 
                      </button>
                    </div>
                  </div>

                  <div className="table-container">
                    <table className="table-custom1 mt-0 overflow-hidden w-100">
                      <thead>
                        <tr>
                          <th className="w-25">
                            <label>User</label>
                          </th>
                          <th className="w-25">
                            <label>Permission Sets</label>
                          </th>
                          <th className="w-25">
                            <label>Created By</label>
                          </th>
                          <th className="w-15">
                            <label>Created At</label>
                          </th>
                          <th className="w-10">
                            <label>Actions</label>
                          </th>
                        </tr>
                      </thead>
                    </table>

                    <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                      <table className="table-custom1 overflow-hidden w-100">
                        <tbody>
                          {iamUsers.length === 0 ? (
                            <div className="w-100 text-center">
                              <p className="ev-small-p-blue mt-5">
                                {isProcessingPopupVisible ? 'Loading Users' : 'No IAM users found'}
                              </p>
                            </div>
                          ) : (
                            iamUsers.map((user) => (
                              <tr key={user.user_id}>
                                <td className="align-top w-25">
                                  <label className="ev-small-p-dark">&nbsp;{user.name}</label><br />
                                  <label>
                                    <button 
                                        className="ev-small-p-blue bg-transparent border-0"  
                                        onClick={() => handleOpenEditForm(user)}
                                      >
                                        <TrimContent text={user.email} maxLength={25} />
                                    </button>
                                  </label>
                                </td>
                                <td className="align-top w-25">
                                  {user.permission_set_names && user.permission_set_names.length > 0 ? (
                                    <div>
                                      {user.permission_set_names.map((setName, index) => (
                                        <span key={index} className="ev-message-card1 me-1 mb-1">  
                                          <TrimContent text={setName} maxLength={25} />
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="ev-small-p-dark">None</span>
                                  )}
                                </td>
                                <td className="align-top w-25 avatar-tooltip" data-tooltip={user.creator_email || 'None'}>
                                  <div className="ev-message-card5 ev-fs-11">
                                    <TrimContent text={user.creator_email} maxLength={25} />
                                  </div>
                                </td>
                                <td className="align-top w-15">{formatDateTime(user.created_at)}</td>
                                <td className="align-top w-10">
                                  <div className="btn-group" role="group">
                                    <button 
                                      type="button"
                                      className="btn ev-btn-skyblue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip"
                                      onClick={() => handleOpenEditForm(user)}
                                      data-tooltip="Edit User"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button 
                                      type="button"
                                      className="btn ev-btn-red-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip"
                                      onClick={() => handleOpenDeleteConfirm(user)}
                                      data-tooltip="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="ev-small-p-dark">
                      Showing {iamUsers.length} of {total} entries
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <i className="bi bi-chevron-left"></i> 
                      </button>
                      <button className="ev-btn-light-fill" disabled>
                        Page {page} of {Math.ceil(total / size)}
                      </button>
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page >= Math.ceil(total / size)}
                        onClick={() => setPage(page + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                    <div>
                      <PaginationControls 
                        initialSize={size}
                        onSizeChange={(newSize) => {
                          setSize(newSize);
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Reusable Delete Confirmation Modal */}
              <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={() => {
                  setShowDeleteConfirm(false);
                  setDeleteUser(null);
                }}
                onConfirm={handleDelete}
                objectName="user"
                itemName={deleteUser ? `${deleteUser.name} (${deleteUser.email})` : ''}
                bodyContent={
                  <p className="text-danger mt-2">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    This action cannot be undone and will remove all associated permissions.
                  </p>
                }
              />
            </div>
          </main>
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default AdminIamUserManagement;